import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Col,
  Link,
  Navbar,
  NavRight,
  Page,
  Popup,
  Row,
  Preloader
} from 'framework7-react';
import II18 from '../translate/II18';
import { gae } from '../helpers/Analytics';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import { getIcon } from '../helpers/utils';
import Checkmark from '../helpers/Checkmark';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import { WEBSOCKET_TOPICS } from '../../redux/middleware/webSocket';
import { startCronJob, stopCronJob } from '../../redux/actions/cronJobs';
import {
  initWebSocket,
  subscribeWebSocket,
  unSubscribeWebSocket
} from '../../redux/actions/websocket';
import { getCurrentHost, REQUEST_TYPE } from '../../lib/utils';
import { waitForCrossDeviceImagesAvailable } from '../../lib/fileUtils';
import {
  beginJourney,
  endJourney,
  JOURNEY,
  setJourneyAttributes,
  getHandoffParentSpanContext
} from '../../telemetry/tracing';
import { withTraceparent } from '../../telemetry/traceparent';
import { forceFlushTelemetry } from '../../telemetry/otel';

export default function CrossDevicePopUp(props) {
  const {
    opened,
    onClose,
    recipientRequestId,
    docId,
    documentOptions,
    onDone,
    onCrossDeviceCreated,
    templateMode
  } = props;

  const dispatch = useDispatch();
  const crossDeviceDocumentCompleted = useSelector(
    state => state.crossDeviceDocumentCompleted
  );

  const [crossDeviceId, setCrossDeviceId] = React.useState(null);
  const [
    errorOnCreateCrossDevice,
    setErrorOnCreateCrossDevice
  ] = React.useState(false);
  const [crossDeviceFinished, setCrossDeviceFinished] = React.useState(false);
  const [applyCrossDeviceData, setApplyCrossDeviceData] = React.useState(false);

  useEffect(() => {
    if (crossDeviceDocumentCompleted === crossDeviceId) {
      checkCrossDeviceStatus(crossDeviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossDeviceDocumentCompleted]);

  useEffect(() => {
    if (opened) {
      dispatch(initWebSocket());
      beginJourney(JOURNEY.HANDOFF, 'handoff.cross_device', {
        'handoff.role': 'host',
        'handoff.template_mode': Boolean(templateMode),
        'document.id_present': Boolean(docId)
      });
      PaperflyAPI.crossDeviceDocumentCreate(
        recipientRequestId,
        docId,
        templateMode,
        documentOptions
      ).then(
        response => {
          setCrossDeviceId(response.id);
          setJourneyAttributes(JOURNEY.HANDOFF, {
            'handoff.created': true
          });
          onCrossDeviceCreated(response.putUrl);
          startCheckingCrossDeviceResult(response.id);
          // Re-flush so open handoff (QR parent) is in Jaeger before guest scan.
          forceFlushTelemetry();
        },
        error => {
          setErrorOnCreateCrossDevice(true);
          endJourney(JOURNEY.HANDOFF, 'error', 'create_failed');
        }
      );
    } else {
      setCrossDeviceId(null);
      setCrossDeviceFinished(false);
      setErrorOnCreateCrossDevice(false);
      setApplyCrossDeviceData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const onPopUpClose = () => {
    gae('CROSS_DEVICE_DOCUMENT_POPUP.CLOSE');
    stopCheckingCrossDeviceResult(crossDeviceId);
    endJourney(JOURNEY.HANDOFF, 'ok', { 'handoff.result': 'closed' });
    onClose();
  };

  const checkCrossDeviceStatus = crossDeviceId => {
    // Return promise so cron poll-quiet covers the async XHR span.
    return PaperflyAPI.crossDeviceDocumentGet(crossDeviceId).then(
      async response => {
        if (
          !window.crossDeviceDocumentFinished &&
          response.crossDeviceDocument?.completed
        ) {
          gae('CROSS_DEVICE_DOCUMENT_POPUP.DOCUMENT_COMPLETED');
          window.crossDeviceDocumentFinished = true;
          stopCheckingCrossDeviceResult(crossDeviceId);
          setApplyCrossDeviceData(true);

          const { pages, documentOptions } = response.crossDeviceDocument;
          const { downloadUrl } = response;

          await waitForCrossDeviceImagesAvailable(pages, downloadUrl);

          endJourney(JOURNEY.HANDOFF, 'ok', {
            'handoff.result': 'completed',
            'handoff.page_count': Array.isArray(pages) ? pages.length : 0
          });

          onDone(pages, documentOptions, downloadUrl, () =>
            setCrossDeviceFinished(true)
          );
        }
      },
      error => {}
    );
  };

  const startCheckingCrossDeviceResult = crossDeviceId => {
    gae('ID_CHECK', 'START_CROSS_DEVICE_CRONJOB');

    window.crossDeviceDocumentFinished = false;

    dispatch(
      subscribeWebSocket(
        WEBSOCKET_TOPICS.CROSS_DEVICE_DOCUMENT_COMPLETED,
        crossDeviceId
      )
    );

    dispatch(
      startCronJob(crossDeviceId, 1000, () =>
        checkCrossDeviceStatus(crossDeviceId)
      )
    );
  };

  const stopCheckingCrossDeviceResult = crossDeviceId => {
    gae('ID_CHECK', 'STOP_CROSS_DEVICE_CRONJOB');

    dispatch(
      unSubscribeWebSocket(WEBSOCKET_TOPICS.CROSS_DEVICE_DOCUMENT_COMPLETED)
    );

    dispatch(stopCronJob(crossDeviceId));
  };

  const url = withTraceparent(
    getCurrentHost() + REQUEST_TYPE.CROSS_DEVICE_DOCUMENT + crossDeviceId,
    getHandoffParentSpanContext()
  );

  return (
    <Popup
      id={'cross_device_popup'}
      className="cross_device_popup"
      animate={false}
      opened={Boolean(opened)}
      swipeToClose={false}
      onPopupClosed={onPopUpClose}
      onPopupOpen={() => gae('IDCHEK_ON_MOBILE_POPUP', 'SHOW')}
      style={{ height: 'auto', marginTop: -215 }}
    >
      <Page style={{ background: '#fff', position: 'relative' }}>
        <Navbar title={II18.get('continue_on_mobile_continue')}>
          <NavRight>
            <Link popupClose>{getIcon('material:clear')}</Link>
          </NavRight>
        </Navbar>

        <div id={'cross_device_url'} data-testid={url} />

        <Row style={{ alignItems: 'center' }}>
          <Col style={{ minHeight: '25vh', textAlign: 'center' }}>
            <p
              style={{
                fontSize: 18,
                fontWeight: 'bold',
                marginBottom: 40
              }}
            >
              <II18>sign_on_mobile_info</II18>
            </p>
            {crossDeviceFinished ? (
              <Checkmark done />
            ) : errorOnCreateCrossDevice ? (
              <p style={{ padding: 40, textAlign: 'center' }}>
                {II18.get('technical_error')}
              </p>
            ) : crossDeviceId && !applyCrossDeviceData ? (
              <QRCode size={120} fgColor={'#333'} level="L" value={url} />
            ) : (
              <Preloader size={62} />
            )}
            {/*url*/}
          </Col>
        </Row>

        <div
          className="dialogBottomBar"
          style={{
            position: 'inherit',
            paddingLeft: '50%',
            display: 'flex',
            justifyContent: 'end',
            margin: 10
          }}
        >
          <Button onClick={onPopUpClose}>
            <II18 get={crossDeviceFinished ? 'title_close' : 'title_cancel'} />
          </Button>
        </div>
      </Page>
    </Popup>
  );
}

CrossDevicePopUp.propTypes = {
  templateMode: PropTypes.bool,
  documentOptions: PropTypes.object,
  opened: PropTypes.any,
  onCrossDeviceCreated: PropTypes.func,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  recipientRequestId: PropTypes.string,
  docId: PropTypes.string
};
